import { isPlainObject } from 'lodash';
import { ASC, DESC } from 'framework/list/baseListHelper';
import { FilterCondition } from 'framework/types';
import { getDefaultCollectionViewParams } from './defaults';
import type { CollectionViewParams, CollectionViewParamsConfig } from './types';

export const restoreCollectionFilters = <Filters>(
  value: unknown,
  config: CollectionViewParamsConfig<Filters>,
): Filters => {
  const fallback = getDefaultCollectionViewParams(config).filters;
  if (!isPlainObject(value)) return fallback;
  const filters = value as Record<string, unknown>;

  const valid = Object.entries(filters).every(([field, conditions]) => {
    if (!isPlainObject(conditions)) return false;
    const entries = Object.entries(conditions as Record<string, unknown>);
    if (entries.length === 0) return false;
    if (!Object.prototype.hasOwnProperty.call(config.filterRules, field)) return false;
    const rule = config.filterRules[field];
    const allowedConditions = ([] as readonly FilterCondition[]).concat(rule.condition);

    return entries.every(([condition, filterValue]) => {
      if (!Object.values(FilterCondition).includes(condition as FilterCondition)) return false;
      if (!allowedConditions.includes(condition as FilterCondition)) return false;
      const values = condition === FilterCondition.IN || condition === FilterCondition.OR ? filterValue : [filterValue];
      return (
        Array.isArray(values) &&
        values.every(item => typeof item === 'string' && (!rule.values || rule.values.includes(item)))
      );
    });
  });

  return valid ? (value as Filters) : fallback;
};

export const restoreCollectionSort = <Filters>(
  value: unknown,
  config: CollectionViewParamsConfig<Filters>,
): CollectionViewParams<Filters>['sort'] => {
  const fallback = getDefaultCollectionViewParams(config).sort;
  if (!isPlainObject(value)) return fallback;
  const sort = value as Record<string, unknown>;
  if (typeof sort.columnId !== 'string' || !sort.columnId.trim()) return fallback;
  if (!config.sortableColumns.includes(sort.columnId)) return fallback;
  const direction = typeof sort.direction === 'string' ? sort.direction.toUpperCase() : undefined;
  if (direction !== ASC && direction !== DESC) return fallback;
  return { columnId: sort.columnId, direction };
};
